import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from '@mui/joy';
import type { DooHardware, StatusFilter } from '../types';
import { saveHardwareFinishes } from '../data/compatibility';
import {
  createHardware,
  createHardwareType,
  deleteHardware,
  deleteHardwareType,
  formatDependents,
  hardwareDependents,
  hardwareTypeDependents,
  listHardware,
  listHardwareCollections,
  listHardwareFinishIds,
  listHardwareFinishes,
  listHardwareTypes,
  updateHardware,
  updateHardwareType,
} from '../data/repository';
import { catalogueStatus, matchesStatusFilter } from '../data/status';
import { NamedEntitySection, useAsyncList } from './NamedEntitySection';
import {
  ActionRow,
  AssetPathField,
  ConfirmDialog,
  DivestFields,
  OracleTable,
  SortableTableHeader,
  sortRowsByText,
  StatusChip,
  StatusFilterSelect,
  TableSortDirection,
  toggleTableSort,
  Toolbar,
  TruncatedText,
} from './FormControls';

type HardwareProductSortKey = 'name' | 'type' | 'collection' | 'assetPath' | 'status';

export function HardwareSection() {
  return (
    <Tabs defaultValue="products">
      <TabList>
        <Tab value="products">Products</Tab>
        <Tab value="types">Hardware types</Tab>
      </TabList>
      <TabPanel value="products" sx={{ px: 0 }}>
        <HardwareProductsPanel />
      </TabPanel>
      <TabPanel value="types" sx={{ px: 0 }}>
        <HardwareTypesPanel />
      </TabPanel>
    </Tabs>
  );
}

function HardwareTypesPanel() {
  const list = useAsyncList(listHardwareTypes);
  return (
    <NamedEntitySection
      title="Hardware types"
      nameLabel="Type name"
      rows={list.rows.map((row) => ({
        id: row.hardware_type_id,
        name: row.hardware_type_name,
      }))}
      loading={list.loading}
      error={list.error}
      onReload={list.reload}
      onCreate={(input) => createHardwareType({ hardware_type_name: input.name })}
      onUpdate={(id, input) => updateHardwareType(id, { hardware_type_name: input.name })}
      onDelete={deleteHardwareType}
      dependentsFor={hardwareTypeDependents}
    />
  );
}

function HardwareProductsPanel() {
  const hardware = useAsyncList(listHardware);
  const types = useAsyncList(listHardwareTypes);
  const collections = useAsyncList(listHardwareCollections);
  const colours = useAsyncList(listHardwareFinishes);
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DooHardware | null>(null);
  const [name, setName] = useState('');
  const [typeId, setTypeId] = useState<string | null>(null);
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [assetPath, setAssetPath] = useState('');
  const [divest, setDivest] = useState(false);
  const [deprecatedDate, setDeprecatedDate] = useState('');
  const [finishIds, setFinishIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<DooHardware | null>(null);
  const [sortKey, setSortKey] = useState<HardwareProductSortKey>('name');
  const [sortDir, setSortDir] = useState<TableSortDirection>('asc');

  const visible = useMemo(
    () => hardware.rows.filter((row) => matchesStatusFilter(row, filter)),
    [hardware.rows, filter]
  );

  const typeName = (id: string | null) =>
    types.rows.find((row) => row.hardware_type_id === id)?.hardware_type_name || '—';
  const collectionName = (id: string | null) =>
    collections.rows.find((row) => row.collection_id === id)?.collection_name || '—';

  const sorted = useMemo(() => {
    return sortRowsByText(visible, (row) => {
      switch (sortKey) {
        case 'name':
          return row.hardware_name;
        case 'type':
          return typeName(row.hardware_type_id);
        case 'collection':
          return collectionName(row.collection_id);
        case 'assetPath':
          return row.asset_path || '';
        case 'status':
          return catalogueStatus(row);
        default:
          return row.hardware_name;
      }
    }, sortDir);
  }, [visible, sortKey, sortDir, types.rows, collections.rows]);

  const onSortColumn = (key: HardwareProductSortKey) => {
    const next = toggleTableSort(sortKey, sortDir, key);
    setSortKey(next.key);
    setSortDir(next.direction);
  };

  useEffect(() => {
    if (!editing) return;
    listHardwareFinishIds(editing.hardware_id).then(setFinishIds).catch((err) => {
      setFormError(err instanceof Error ? err.message : 'Failed to load finishes');
    });
  }, [editing]);

  const payload = () => ({
    hardware_name: name.trim(),
    hardware_type_id: typeId,
    collection_id: collectionId,
    asset_path: assetPath.trim() || null,
    divest,
    deprecated_date: deprecatedDate || null,
  });

  return (
    <Box>
      {hardware.error ? <Alert color="danger" sx={{ mb: 2 }}>{hardware.error}</Alert> : null}
      {formError && !open ? <Alert color="warning" sx={{ mb: 2 }}>{formError}</Alert> : null}
      <Toolbar>
        <StatusFilterSelect value={filter} onChange={setFilter} />
        <Button
          onClick={() => {
            setEditing(null);
            setName('');
            setTypeId(null);
            setCollectionId(null);
            setAssetPath('');
            setDivest(false);
            setDeprecatedDate('');
            setFinishIds([]);
            setFormError(null);
            setOpen(true);
          }}
        >
          Add hardware
        </Button>
      </Toolbar>
      <OracleTable>
        <thead>
          <tr>
            <SortableTableHeader
              label="Hardware name"
              active={sortKey === 'name'}
              direction={sortDir}
              onSort={() => onSortColumn('name')}
            />
            <SortableTableHeader
              label="Type"
              active={sortKey === 'type'}
              direction={sortDir}
              onSort={() => onSortColumn('type')}
            />
            <SortableTableHeader
              label="Collection"
              active={sortKey === 'collection'}
              direction={sortDir}
              onSort={() => onSortColumn('collection')}
            />
            <SortableTableHeader
              label="Asset path"
              active={sortKey === 'assetPath'}
              direction={sortDir}
              onSort={() => onSortColumn('assetPath')}
              style={{ width: 280 }}
            />
            <SortableTableHeader
              label="Status"
              active={sortKey === 'status'}
              direction={sortDir}
              onSort={() => onSortColumn('status')}
            />
            <th style={{ width: 140 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {hardware.loading ? (
            <tr><td colSpan={6}>Loading…</td></tr>
          ) : sorted.length === 0 ? (
            <tr><td colSpan={6}>No hardware yet.</td></tr>
          ) : (
            sorted.map((row) => (
              <tr key={row.hardware_id}>
                <td>{row.hardware_name}</td>
                <td>{typeName(row.hardware_type_id)}</td>
                <td>{collectionName(row.collection_id)}</td>
                <td>
                  <TruncatedText value={row.asset_path} maxWidth={260} />
                </td>
                <td><StatusChip status={catalogueStatus(row)} /></td>
                <td>
                  <ActionRow>
                    <Button
                      size="sm"
                      variant="plain"
                      onClick={() => {
                        setEditing(row);
                        setName(row.hardware_name);
                        setTypeId(row.hardware_type_id);
                        setCollectionId(row.collection_id);
                        setAssetPath(row.asset_path || '');
                        setDivest(Boolean(row.divest));
                        setDeprecatedDate(row.deprecated_date?.slice(0, 10) || '');
                        setFormError(null);
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={async () => {
                        const dependents = await hardwareDependents(row.hardware_id);
                        if (dependents.length) {
                          setFormError(
                            `Cannot delete “${row.hardware_name}” while it is used by ${formatDependents(dependents)}. Divest or deprecate it instead.`
                          );
                          return;
                        }
                        setDeleteRow(row);
                      }}
                    >
                      Delete
                    </Button>
                  </ActionRow>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </OracleTable>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ minWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="title-lg">{editing ? 'Edit hardware' : 'New hardware'}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {formError ? <Alert color="danger">{formError}</Alert> : null}
            <Input placeholder="Hardware name" value={name} onChange={(e) => setName(e.target.value)} />
            <Select
              placeholder="Hardware type"
              value={typeId}
              onChange={(_, value) => setTypeId((value as string) || null)}
            >
              {types.rows.map((row) => (
                <Option key={row.hardware_type_id} value={row.hardware_type_id}>
                  {row.hardware_type_name}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Collection"
              value={collectionId}
              onChange={(_, value) => setCollectionId((value as string) || null)}
            >
              {collections.rows.map((row) => (
                <Option key={row.collection_id} value={row.collection_id}>
                  {row.collection_name}
                </Option>
              ))}
            </Select>
            <AssetPathField value={assetPath} onChange={setAssetPath} />
            <DivestFields
              divest={divest}
              deprecatedDate={deprecatedDate}
              onDivest={setDivest}
              onDeprecatedDate={setDeprecatedDate}
            />
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Available finishes
              </Typography>
              <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
                Finishes defined here are what customers can choose when this hardware is allowed on a door.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.5 }}>
                {colours.rows.map((finish) => (
                  <Checkbox
                    key={finish.finish_id}
                    label={finish.finish_name}
                    checked={finishIds.includes(finish.finish_id)}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setFinishIds((current) =>
                        checked
                          ? [...current, finish.finish_id]
                          : current.filter((id) => id !== finish.finish_id)
                      );
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!name.trim()) {
                    setFormError('Hardware name is required.');
                    return;
                  }
                  try {
                    const saved = editing
                      ? await updateHardware(editing.hardware_id, payload())
                      : await createHardware(payload());
                    await saveHardwareFinishes(saved.hardware_id, finishIds);
                    setOpen(false);
                    hardware.reload();
                  } catch (err) {
                    setFormError(err instanceof Error ? err.message : 'Save failed');
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteRow)}
        title="Delete hardware?"
        message={deleteRow ? `Delete “${deleteRow.hardware_name}”? Prefer deprecation for established products.` : ''}
        onClose={() => setDeleteRow(null)}
        onConfirm={async () => {
          if (!deleteRow) return;
          try {
            await deleteHardware(deleteRow.hardware_id);
            setDeleteRow(null);
            hardware.reload();
          } catch (err) {
            setDeleteRow(null);
            setFormError(err instanceof Error ? err.message : 'Delete failed');
          }
        }}
      />
    </Box>
  );
}
